import type { Document } from '../../services/documentService';
import type { ApiDocument } from './types';

/**
 * Project a `listDocuments` response row (snake_case `Document`) into the
 * `ApiDocument` shape the reducer consumes (`apiDocumentToState`).
 *
 * Extracted from the `useDocuments` fetch loop so the projection — including the
 * #1965 B2b-prep `is_multi_document` marker — is unit-testable without a hook
 * harness. Type-only imports keep this module free of runtime dependencies.
 */
export function apiDocumentFromResponse(doc: Document): ApiDocument {
  return {
    doc_id: doc.doc_id,
    job_id: doc.job_id || null,
    filename: doc.filename,
    status: doc.status,
    document_type: doc.document_type,
    document_date: doc.document_date,
    description: doc.description,
    // #1965 B2b-prep: carry the dedicated split-parent marker through so the
    // reducer/selector dual-read has an authoritative standalone signal — the
    // one that outlives the 28.6 legacy `document_type='multi-document'` DROP.
    is_multi_document: doc.is_multi_document,
    parent_source: doc.parent_source,
    error_message: doc.error_message,
    uploaded_at: doc.uploaded_at,
    // Taxonomy-based classification. taxonomy-v2 is the sole render path: the
    // row component renders a single `category`/`subcategory` label, so we feed
    // it the v2 (domain, role) cell names. The legacy v1 `?? doc.category`
    // fallback was removed with the classifier-v1 decommission (#2009).
    category: doc.domain_name,
    subcategory: doc.role_name,
    // taxonomy-v2 native cell (#1454): thread the dedicated (domain, role)
    // fields through so getDisplayType can prefer `role_name` as the row
    // title — independent of the category/subcategory fold above.
    domain: doc.domain,
    role: doc.role,
    domain_name: doc.domain_name,
    role_name: doc.role_name,
    tier: doc.tier,
    // Icon name from feedDisplay.icon in document type YAML
    icon: doc.icon,
    // Display name from document type YAML
    document_type_display_name: doc.document_type_display_name,
    // Pre-computed highlight bubbles for folded view (JSON string from backend)
    highlighted_facts: doc.highlighted_facts,
    // Human-readable processing status for page refresh recovery
    processing_status: doc.processing_status,
    // User-defined tags (JSON array of tag IDs)
    tags: doc.tags,
  };
}
